import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, toApiError } from "../../shared/api/queryClient";
import * as api from "./api";
import type { Holiday, HolidayInput, HolidaysQuery } from "./types";

const wrap =
  <T,>(fn: () => Promise<T>, fallback: string) =>
  async (): Promise<T> => {
    try {
      return await fn();
    } catch (e) {
      throw toApiError(e, fallback);
    }
  };

export const holidayKeys = {
  all: ["calendar", "holidays"] as const,
  list: (q: HolidaysQuery) => ["calendar", "holidays", q] as const,
};
/** Mesma chave do bloco de sazonalidade do Dashboard (o "esperado" muda com o calendário). */
const SEASONALITY_KEY = ["dashboard", "seasonality"] as const;

export function useHolidays(q: HolidaysQuery) {
  return useQuery<Holiday[], ApiError>({
    queryKey: holidayKeys.list(q),
    queryFn: wrap(() => api.fetchHolidays(q), "Os feriados não carregaram"),
    placeholderData: keepPreviousData,
  });
}

/** Toda escrita invalida a lista de feriados e o gráfico de sazonalidade. */
export function useHolidayMutations() {
  const qc = useQueryClient();
  const onSuccess = () => Promise.all([qc.invalidateQueries({ queryKey: holidayKeys.all }), qc.invalidateQueries({ queryKey: SEASONALITY_KEY })]);
  return {
    create: useMutation<Holiday, ApiError, HolidayInput>({ mutationFn: (i) => wrap(() => api.createHoliday(i), "O feriado não foi salvo")(), onSuccess }),
    deactivate: useMutation<Holiday, ApiError, { id: number; reason: string }>({
      mutationFn: ({ id, reason }) => wrap(() => api.deactivateHoliday(id, reason), "O feriado não foi desativado")(),
      onSuccess,
    }),
    reactivate: useMutation<Holiday, ApiError, number>({ mutationFn: (id) => wrap(() => api.reactivateHoliday(id), "O feriado não foi reativado")(), onSuccess }),
  };
}

export type HolidayMutations = ReturnType<typeof useHolidayMutations>;
